/**
 * SmsConsentTrigger, decides whether to show the SMS consent modal to
 * the current user, and renders it when eligible.
 *
 * Self-contained: queries the user's profile on mount, evaluates trigger
 * conditions, and shows the modal if all match. After the modal closes
 * (any decision), local state suppresses re-rendering for the rest of
 * the session.
 *
 * Trigger conditions (all must be true):
 *   - User has a phone_e164 on file (no point asking for SMS otherwise)
 *   - sms_consent_prompt_seen_at IS NULL (haven't shown them yet)
 *   - sms_consent_at IS NULL (haven't already opted in)
 *   - sms_opt_out_at IS NULL (haven't already opted out)
 *
 * Mounted inside AppGate so it only ever runs for authenticated users
 * with confirmed access. Doesn't block render, modal appears as an
 * overlay after a brief check, no full-page loading state.
 */

import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { SmsConsentModal } from './SmsConsentModal';

export default function SmsConsentTrigger() {
  const [shouldShow, setShouldShow] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || cancelled) return;

      const { data: profile, error } = await supabase
        .from('profiles')
        .select('phone_e164, sms_consent_at, sms_opt_out_at, sms_consent_prompt_seen_at')
        .eq('id', user.id)
        .maybeSingle();

      if (error || !profile || cancelled) return;

      const eligible =
        !!profile.phone_e164 &&
        profile.sms_consent_prompt_seen_at == null &&
        profile.sms_consent_at == null &&
        profile.sms_opt_out_at == null;

      if (eligible) {
        setUserId(user.id);
        setShouldShow(true);
      }
    };

    check();

    return () => {
      cancelled = true;
    };
  }, []);

  if (!shouldShow || !userId) return null;

  return (
    <SmsConsentModal
      open={shouldShow}
      userId={userId}
      onClose={() => setShouldShow(false)}
    />
  );
}
